// Percorso file: src/pages/home/HomeParent.jsx
//
// Home del ruolo PARENT — ricostruita (cantiere HOME-PARENT).
//
// La pagina ha due blocchi: la griglia dei figli e le voci.
//
// ⚠️ LA VIA DEI DATI NON È PIÙ QUELLA DELLA VECCHIA PARENT. La pagina
// demolita in `2462842` scaricava l'anagrafica INTERA del club con
// `GET /users` e ci pescava dentro i figli: quella rotta oggi è
// riservata ad ADMIN e SUPERADMIN (server.js, `soloAdminPerElenco`), e
// a un genitore risponde 403. Qui si fa come HomeIncomplete.jsx, che
// il lavoro lo fa già:
//   1. gli id dei figli da `linkedProfiles`, che è GIÀ dentro `user`
//      di AuthContext — nessuna chiamata (`idsDeiFigli`, importata da
//      utils/famiglia.js: la regola di lettura resta una);
//   2. una `GET /users/:id` per figlio, in parallelo, con un esito per
//      figlio: un figlio che risponde 403/404 diventa una tessera in
//      errore e non porta con sé le altre. Le tessere però NON si
//      riempiono una per una: si attende `Promise.all`, e passano da
//      «Loading…» al contenuto tutte insieme. L'isolamento degli errori
//      è per riga, la progressione no;
//   3. una `GET /teams` sola, per la mappa nome→categoria della
//      pastiglia (§2). È aperta a ogni utente autenticato.
//
// ⚠️ LA SQUADRA DEL FIGLIO STA NELLA SUA TESSERA, non sul suo documento. La
// `GET /users/:id` del punto 2 restituisce i `contexts` così come stanno su
// Firestore (server.js) e la squadra si legge di lì, dalla coppia
// canonica `{teams, allTeams}` della tessera dell'anno OPERATIVO. Il campo di
// persona `team` non si legge più: Team Assignment non lo scrive, ed era lui a
// far dire «No team yet» a figli che una squadra ce l'hanno.
//
// LA TESSERA PIENA È L'INGRESSO DELLA VISTA DELEGATA (cantiere
// HOME-CHILD): si preme e si apre `/home/child/<id del figlio>`, dove il
// genitore vede la home del figlio in SOLA LETTURA. Nessuna
// impersonazione: l'utente collegato resta lui, e l'id viaggia nella
// rotta. Restano non premibili le tessere in attesa e in errore — non c'è
// nessuna scheda da aprire dietro di loro.
//
// Il controllo d'accesso NON sta qui: sta in App.jsx, nel cancello
// condiviso RequireRole. Questa è una home di primo livello, quindi
// nessun «Back» nella banda (doc 07 §7.2). Barra utente e «Switch role»
// li porta il telaio: la pagina non li ridisegna.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaComments, FaUserCircle, FaShoppingBag, FaLifeRing } from "react-icons/fa";
import HomeShell from "../../components/HomeShell";
import AuthAvatar from "../../components/AuthAvatar";
import { PastigliaCategoriaSeNota } from "../../components/PastigliaCategoria";
import { SOCIAL, SHOP_URL } from "../../components/clubLinks";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api/api";
import { normalizza } from "../../utils/roleUtils";
// LA SEDE UNICA DELLA LETTURA DI SQUADRA (cantiere «lettore unico»): qui
// arriva, gia' scritta, l'unione che questa pagina teneva in copia locale.
// `valeSuTutteLeSquadre` non si importa piu': la legge il modulo, dentro il
// ripiego di `squadreDellaTessera`.
import { squadreDelSoggetto } from "../../utils/squadre";
import { idsDeiFigli } from "../../utils/famiglia";
import EnrolNextSeason from "./EnrolNextSeason";
import "./HomeParent.css";

/** La tessera di un figlio di cui si conosce l'id e nient'altro. */
const tesseraInAttesa = (id) => ({
  id,
  stato: "caricamento",
  documento: null,
  negato: false
});

/* ===============================
   LA SQUADRA DEL FIGLIO — le copie di pagina NON CI SONO PIU'

   Qui vivevano `annoDiLista`, `squadreDellaTessera`, `tesseraValida` e
   `squadreDelFiglio`: copie dichiarate delle gemelle di
   `MemberProfileModal.jsx`, in attesa — parole del commento che stava qui —
   del «modulo condiviso, materia del cantiere lettore unico». Quel modulo
   ora esiste, e sono due: `utils/stagione.js` per l'anno,
   `utils/squadre.js` per la tessera e le sue squadre. I corpi sono quelli
   di qui, invariati: non e' stata riscritta una riga, sono state tolte
   quattro copie.

   `squadreDelFiglio(documento, anno)` e' diventata
   `squadreDelSoggetto(documento?.contexts, anno)`: stesso corpo, stesse
   opzioni predefinite — solo tessere ACTIVE, tessera senz'anno esclusa,
   NESSUNA tessera amministrativa (quella eccezione serve a mostrare le
   cariche nella scheda, e qui si cerca una squadra e basta).
=============================== */

const HomeParent = () => {
  // ⚠️ `operatingSeason` viene dalla stessa cassettina di `user`: l'ANNO
  // OPERATIVO del club, gia' normalizzato a intero da AuthContext. Lo
  // prendono di qui, con la stessa riga, UsersList, TeamAssignment e
  // MemberProfileModal: nessuna chiamata propria a `GET /season-config`, e un
  // solo posto che dice l'annata. Fuori da `seasonStatus: "ready"` vale
  // `null`, e il predicato dell'anno lo tratta come «annata ignota».
  const { user, operatingSeason } = useAuth();
  const navigate = useNavigate();

  // L'unico nullo che il predicato dell'anno conosce.
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  // ⚠️ CHIAVE STABILE, non l'oggetto `user` (stessa avvertenza di
  // HomeIncomplete.jsx): `refreshUserFromBackend` restituisce un
  // oggetto NUOVO a ogni chiamata, e un effetto che dipendesse da `user`
  // rileggerebbe i figli a ogni giro. Si dipende dagli id, in una stringa.
  const idsFigli = useMemo(() => idsDeiFigli(user), [user]);
  const chiaveFigli = idsFigli.join("|");

  // Un elemento per figlio:
  //   { id, stato: "caricamento" | "ok" | "errore", documento, negato }
  const [figli, setFigli] = useState([]);

  // Le squadre, come arrivano da GET /teams: servono solo alla mappa
  // nome→categoria qui sotto.
  const [squadre, setSquadre] = useState([]);

  // LETTURA DELLE SCHEDE DEI FIGLI — una GET per figlio, e non c'è
  // alternativa (vedi l'avvertenza in testa al file). Il permesso è il ramo
  // TUTORE di GET /users/:id, che guarda i `guardians` del FIGLIO: una voce
  // di `linkedProfiles` senza il legame di ritorno risponde 403, e la
  // tessera lo dice invece di sparire.
  // ⚠️ La dipendenza è `chiaveFigli` (una stringa) e NON `idsFigli`, che è
  // un array nuovo a ogni giro di `user`: è la stessa avvertenza di sopra.
  // L'effetto però LEGGE `idsFigli`, che è l'elenco vero — rifabbricarlo con
  // uno `split("|")` dalla chiave significherebbe scomporre a mano ciò che
  // si ha già in mano, e legare la lettura dei figli alla forma della
  // chiave. La regola di esaustività delle dipendenze qui va quindi
  // silenziata di proposito: quando cambia `idsFigli` cambia `chiaveFigli`,
  // perciò l'effetto rigira comunque.
  useEffect(() => {
    if (idsFigli.length === 0) {
      setFigli([]);
      return undefined;
    }

    let annullato = false;

    setFigli(idsFigli.map(tesseraInAttesa));

    Promise.all(
      idsFigli.map(async (id) => {
        try {
          const documento = await api.get(`/users/${encodeURIComponent(id)}`);
          return { id, stato: "ok", documento, negato: false };
        } catch (err) {
          // 403 (nessun legame di ritorno) e 404 (documento sparito) sono lo
          // stesso guaio per chi guarda: la scheda non è raggiungibile e la
          // ripara la segreteria. Gli altri sono guasti di rete.
          const negato = err && (err.status === 403 || err.status === 404);
          return { id, stato: "errore", documento: null, negato };
        }
      })
    ).then((esiti) => {
      if (!annullato) setFigli(esiti);
    });

    return () => {
      annullato = true;
    };
    // Il silenziamento sta QUI e non sopra `useEffect`: react-hooks segnala
    // sull'ARRAY delle dipendenze, quindi è questa la riga da coprire. Il
    // perché è nell'avvertenza in testa all'effetto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiaveFigli]);

  // LE SQUADRE, una volta sola. Se la chiamata fallisce non si avvisa e non
  // si riprova: la categoria è un di più sulla tessera, e senza mappa la
  // pastiglia semplicemente tace (è ciò che fa già PastigliaCategoriaSeNota
  // quando la categoria non è nota). Il nome della squadra, che è il dato
  // che conta, arriva dalla tessera del figlio e resta a schermo comunque.
  useEffect(() => {
    let annullato = false;

    api
      .get("/teams")
      .then((dati) => {
        if (!annullato) setSquadre(Array.isArray(dati) ? dati : []);
      })
      .catch(() => {
        /* nessuna categoria: la pastiglia tace */
      });

    return () => {
      annullato = true;
    };
  }, []);

  // Categoria della squadra di una persona (doc 07 §2). Stessa logica di
  // `categoriaDi` in TeamAssignment.jsx: la categoria NON sta sull'utente,
  // sta sulla SQUADRA, e la mappa è per NOME perché la tessera porta il nome
  // della squadra, non il suo id.
  const categoriaDi = useMemo(() => {
    const perNome = new Map(
      squadre
        .filter((t) => t && typeof t === "object" && t.name)
        .map((t) => [t.name, normalizza(t.category)])
    );
    return (nomeSquadra) => perNome.get(nomeSquadra) || "";
  }, [squadre]);

  // LE TESSERE DA DISEGNARE. Il vuoto e l'attesa non si leggono nello stesso
  // posto: «nessun figlio» lo dice `idsFigli`, che è già noto al PRIMO
  // render perché viene da `user`; `figli` invece è vuoto anche nel giro fra
  // il primo render e la prima esecuzione dell'effetto. Leggere il vuoto da
  // `figli` faceva lampeggiare «No children» a un genitore che i figli ce li
  // ha. Finché l'effetto non ha scritto, le tessere si ricavano dagli id: un
  // genitore con figli vede «Loading…», mai una griglia vuota.
  const tessere = useMemo(
    () => (figli.length > 0 ? figli : idsFigli.map(tesseraInAttesa)),
    [figli, idsFigli]
  );

  return (
    <HomeShell>
      <h1 className="qpsc-page-title">QPSC Parent Area</h1>

      {/* §7.3 i quattro canali del club: glifi oro nudi, su una riga
          centrata. Stesso markup e stessi indirizzi della home del
          SuperAdmin — gli indirizzi arrivano dalla sede unica,
          components/clubLinks.js, e non si ricopiano. */}
      <div className="qpsc-social">
        {SOCIAL.map(({ href, title, Icon }) => (
          <a
            key={title}
            className="qpsc-social__link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
            aria-label={title}
          >
            <Icon />
          </a>
        ))}
      </div>

      {/* VOCI E FIGLI SI ALTERNANO (rev. utente): Messaging sta SOPRA i
          figli, lo Shop e My Profile sotto. Da qui i DUE `<nav>` invece di
          uno: il blocco dei figli si incunea fra le voci, e un elenco di
          tessere non è navigazione — dentro il `<nav>` non ci può stare. */}
      <nav className="qpsc-menu qpsc-parent-menu">
        {/* LIVE CHAT, ACCESA (cantiere LIVE CHAT): naviga a /messaging, la
            rotta della pagina Messaging, ora ricostruita e dietro
            <PrivateRoute> (App.jsx). La forma è quella delle altre voci
            vive della pagina — «My Profile» più sotto: `<button>` con
            `onClick` e `navigate`, mai un `<Link>` — e l'etichetta perde
            la coda « — coming soon ».
            ⚠️ SEQUENZA DEL TRAVASO IN PRODUZIONE: questa accensione è per
            il COLLAUDO IN BETA (dati finti). In produzione il bottone va
            acceso INSIEME al deploy delle regole di sicurezza della chat
            (il recinto), MAI PRIMA: un socio non deve poter entrare in una
            chat il cui recinto non è ancora attivo. */}
        <button
          type="button"
          className="qpsc-btn-gold"
          onClick={() => navigate("/messaging")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaComments />
          </span>
          Live Chat
        </button>
      </nav>

      {/* IL RINNOVO (YS-11.2), sotto la Live Chat e sopra i figli. Si mostra
          da sé solo a stagioni sovrapposte e a iscrizioni aperte — la
          condizione sta tutta dentro il componente, che legge la cassettina
          della stagione da `useAuth()`: qui non c'è nulla da decidere.
          Sta FUORI dai due `<nav>`, come il blocco dei figli e per la stessa
          ragione: non è una voce di navigazione: apre un pannello e scrive. */}
      <EnrolNextSeason />

      {/* I FIGLI, fra Messaging e lo Shop. Una COLONNA di tessere impilate,
          una per riga e a tutta larghezza come i pulsanti — non più una
          griglia: la disposizione è quella della `child-card` demolita in
          `2462842` (foto a sinistra, nome e squadra a destra), con la veste
          nuova (superficie, raggio delle card, nessun bordo oro, nessuna
          emoji) e la pastiglia. */}
      {idsFigli.length === 0 ? (
        <p className="qpsc-body-text qpsc-body-text--muted qpsc-parent-empty">
          No children are linked to your account yet.
        </p>
      ) : (
        <ul className="qpsc-parent-children">
          {tessere.map((figlio) => {
            if (figlio.stato === "caricamento") {
              return (
                <li key={figlio.id}>
                  <div className="qpsc-parent-child qpsc-parent-child--quiet">
                    <span className="qpsc-parent-child__name">Loading…</span>
                  </div>
                </li>
              );
            }

            if (figlio.stato === "errore") {
              return (
                <li key={figlio.id}>
                  <div className="qpsc-parent-child qpsc-parent-child--quiet">
                    <span className="qpsc-parent-child__name">
                      A child&rsquo;s record
                    </span>
                    <span className="qpsc-parent-child__note">
                      {figlio.negato
                        ? "This record is not linked to you, or it no longer exists. Please contact the club."
                        : "This record could not be loaded. Please check your connection and try again."}
                    </span>
                  </div>
                </li>
              );
            }

            const documento = figlio.documento || {};
            const nome = [documento.firstName, documento.lastName]
              .filter(Boolean)
              .join(" ")
              .trim();
            // LA SQUADRA, DALLA TESSERA DELL'ANNO OPERATIVO — non piu' dal
            // campo di persona `documento.team`, che Team Assignment non
            // scrive: la squadra si assegna sulla TESSERA, ed era quello il
            // «No team yet» su figli che una squadra ce l'hanno.
            // Un figlio ha, di norma, una tessera sola: il nome della squadra
            // e' il PRIMO dell'unione. «Tutte le squadre» non e' un nome e non
            // se ne inventa uno: si dice a schermo con la stessa parola della
            // scheda («All teams»), e la pastiglia tace perche' non c'e'
            // nessuna squadra da cercare.
            const { nomi, tutte } = squadreDelSoggetto(
              documento?.contexts,
              annoOperativo
            );
            const nomeSquadra = tutte ? "" : nomi[0] || "";

            // La tessera piena è PREMIBILE e apre la vista delegata del
            // figlio. Non è un <button>: si usa lo schema già in uso
            // nell'app per le schede premibili (Enrol.jsx) —
            // `role="button"`, `tabIndex` e la tastiera (Invio e barra
            // spaziatrice), senza il quale chi naviga senza mouse non
            // potrebbe aprire il figlio.
            // ⚠️ IL PREMIBILE STA DENTRO IL <li>, MAI SUL <li>. Un
            // `role="button"` sul <li> gli TOGLIE il ruolo nativo di «voce
            // d'elenco»: il lettore di schermo smette di annunciare
            // «elenco, 3 elementi» e la <ul> non è più un elenco per nessuno.
            // Il <li> resta quindi nudo — è la voce — e gli attributi
            // premibili scendono sul <div> interno, che è quello che porta
            // la classe `.qpsc-parent-child`. L'aspetto non cambia di un
            // pixel e nessun CSS è toccato: il <li> è un figlio flex della
            // colonna `.qpsc-parent-children` e si allarga da sé
            // (`align-items: stretch`), e il <div> dentro ha `width: 100%`
            // come prima.
            const apri = () => navigate(`/home/child/${encodeURIComponent(figlio.id)}`);

            return (
              <li key={figlio.id}>
                <div
                  className="qpsc-parent-child"
                  role="button"
                  tabIndex={0}
                  onClick={apri}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      apri();
                    }
                  }}
                >
                  {/* La foto si scarica con fetch autenticata: lo Storage è
                      chiuso in lettura. Senza `photoKey` AuthAvatar non
                      chiama nemmeno il backend e va diretto al ripiego, che
                      sono le INIZIALI — nessun `fallback` con emoji, che il
                      doc 07 §5 abolisce. */}
                  <AuthAvatar
                    userId={figlio.id}
                    photoKey={documento.photoUrl || ""}
                    name={nome}
                    alt={nome || "Child"}
                    className="qpsc-parent-child__photo"
                  />

                  {/* La colonna di testo alla DESTRA della foto: in alto il
                      nome, sotto la squadra con la pastiglia. È l'equivalente
                      di `.child-info` della tessera vecchia. Serve un
                      contenitore proprio perché la tessera ora è orizzontale
                      (foto | testo) e le due righe di testo devono impilarsi
                      fra loro, non accanto alla foto. */}
                  <span className="qpsc-parent-child__text">
                    {/* Il nome è una riga a sé: nessun gruppo attorno, perché
                        non ha più nulla accanto a sé da allineare. */}
                    <span className="qpsc-parent-child__name">
                      {nome || "A child's record"}
                    </span>

                    {/* §2 SQUADRA e pastiglia sono UN gruppo orizzontale (rev.
                        utente): la pastiglia sta sulla stessa riga della
                        squadra, alla sua destra, centrata in verticale (stesso
                        schema di `.qpsc-person__title` negli strumenti admin).
                        La pastiglia TACE se la categoria non è nota — un
                        figlio non ancora assegnato non esibisce un timbro
                        «NO CATEGORY» ai genitori — e in quel caso il gruppo
                        contiene la sola squadra; con «No team yet» la
                        categoria non è nota per definizione, quindi la riga
                        resta muta.
                        Squadra non ancora assegnata: nessuna tessera dell'anno
                        operativo porta un nome di squadra. Si scrive «No team
                        yet» e non «Pending», che nel modello nuovo è uno STATO
                        e vuol dire un'altra cosa. */}
                    <span className="qpsc-parent-child__teamline">
                      <span className="qpsc-parent-child__team">
                        {tutte ? "All teams" : nomeSquadra || "No team yet"}
                      </span>
                      <PastigliaCategoriaSeNota category={categoriaDi(nomeSquadra)} />
                    </span>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Le voci SOTTO i figli: lo Shop, My Profile e, ultimo pulsante
          della pagina, «More / Assistance». Nessuna spaziatura maggiorata
          sul gruppo — la porta la sola My Profile, che apre il blocco di
          chiusura. */}
      <nav className="qpsc-menu">
        {/* Unica voce esterna, e unica variante d'accento della pagina
            (§5.1: al massimo una — My Profile qui sotto resta un pulsante
            oro NORMALE). Le icone sono Font Awesome su TUTTE le voci, mai
            emoji: regola tutto-o-niente del §5. */}
        <a
          className="qpsc-btn-gold qpsc-btn-gold--accent"
          href={SHOP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="qpsc-btn-gold__icon">
            <FaShoppingBag />
          </span>
          QPSC Online Shop
        </a>

        {/* Voce INTERNA: `<button>` con `navigate`, mai un `<Link>`
            (schema di HomeSuperadmin). La destinazione è la stessa che
            le home vecchie davano a «My Profile», /myprofile, con la
            stessa icona. Apre il blocco di chiusura (con «More /
            Assistance» sotto e il banner): da qui la spaziatura
            maggiorata di `qpsc-parent-closing`. */}
        <button
          type="button"
          className="qpsc-btn-gold qpsc-parent-closing"
          onClick={() => navigate("/myprofile")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaUserCircle />
          </span>
          My Profile
        </button>
        <button
          type="button"
          className="qpsc-btn-gold"
          onClick={() => navigate("/more")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaLifeRing />
          </span>
          More / Assistance
        </button>
      </nav>

      {/* BANNER DI CHIUSURA (rev. utente), in coda dopo il menu. La resa è
          del telaio, `.qpsc-adbanner` (HomeShell.css): una classe sola per
          tutte le home che lo mostrano. NON è cliccabile: nessun `<a>`,
          nessun `onClick`, nessun `role` — è un'immagine e basta, e sta
          FUORI dal `<nav>` proprio perché non è una voce di navigazione.
          L'immagine è servita da `public/`, quindi non si importa: il
          percorso è assoluto e l'URL è CODIFICATO, perché il file sul
          disco si chiama «footer AG.png», con uno spazio nel nome, e in
          un URL lo spazio è `%20`. */}
      <img
        className="qpsc-adbanner"
        src="/assets/footer%20AG.png"
        alt="AustraliaGoal"
      />
    </HomeShell>
  );
};

export default HomeParent;
